import React from "react";
import { useGetState, useSetState } from "components/ReduxProvider";
import { useMatchMain } from "root/RootRouter";
import { windowReloadLimited } from "tools/windowReload";
import Splash from "root/auth/Splash";
import { getStoragedJsVersion, setStoragedJsVersion } from "tools/storageJsVersion";
import { useI18NextIsReady } from "tools/i18next";
import { useViewerQuery } from "queries/viewerQuery";
import { useLocation } from "react-router-dom";
import { ASSISTANT_HASH } from "../assistant/AssistantPopup";
import { useEffectOnce } from "react-use";
import { deleteJsStorage, downloadJsVersion, updateJsStorage } from "tools/jsUpdater";
import { sentryCapture } from "sentry/sentry";
import { EVENT } from "tools/events";
import { useEvent } from "react-use";

type Props = {
  children: React.ReactNode;
};

const ReleaseUpdater: React.FC<Props> = ({ children }) => {
  const mainRoute = useMatchMain({ end: true });
  const releaseId = useGetState("releaseId");
  const [releaseIdPrev, setReleaseIdPrev] = React.useState(getStoragedJsVersion());
  const i18nIsReady = useI18NextIsReady();
  const { viewer } = useViewerQuery();
  const token = useGetState("token");
  const isReleaseReady: boolean = i18nIsReady && (!!viewer || !token);
  const reloadOnce = React.useRef(false);
  const setNeedsUpdate = useSetState("needsUpdate");
  const location = useLocation();
  const isAssistantOpen = location.hash === ASSISTANT_HASH;
  const [reloadNeeded, setReloadNeeded] = React.useState(false);

  const isDownloadingRef = React.useRef(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const [forceDownloadUpdate, setForceDownloadUpdate] = React.useState(false);
  useEffectOnce(() => {
    window.reactUpdaterLoaded = true; // let cordova know that we've loaded the updater, so cordova will not try to download new version
  });

  // releaseIdPrev === "" - it is empty on first load in browser, so it's the latest version
  // releaseIdPrev.indexOf("anticache-") === 0 - it is anticache on first load as an app, so it's the latest version
  const isFirstLoad = releaseIdPrev === "" || releaseIdPrev.indexOf("anticache-") === 0;
  const isLatest = isFirstLoad || releaseId === releaseIdPrev;

  React.useEffect(() => {
    if (releaseId !== releaseIdPrev) {
      if (isFirstLoad) {
        setReleaseIdPrev(releaseId);
        setStoragedJsVersion(releaseId);
      }
      console.log("[ReleaseUpdater] Release ID changed from", releaseIdPrev, "to", releaseId);
    }
  }, [releaseId, releaseIdPrev, isFirstLoad]);

  useEvent(EVENT.forceDownloadUpdate, () => {
    console.log("[ReleaseUpdater] Force update app");
    setForceDownloadUpdate(true);
  });

  // Handle download and store js
  React.useEffect(() => {
    if (isDownloadingRef.current || (!forceDownloadUpdate && isLatest) || reloadNeeded || retryCount >= 3) return;

    if (!window.appversion) {
      // Running in browser (not in Cordova app) - directly update stored version without download
      // We avoid checking window.cordova as it may not be available due to initialization failures
      console.log("[ReleaseUpdater] Running in browser, directly updating stored version to reload");
      setStoragedJsVersion(releaseId);
      setReloadNeeded(true);
      return;
    }

    console.log("[ReleaseUpdater] Initiating download for new version");
    isDownloadingRef.current = true;

    const abortController = new AbortController();

    downloadJsVersion(releaseId, abortController.signal)
      .then((js) => {
        updateJsStorage(js, releaseId)
          .then(() => {
            console.log("[ReleaseUpdater] Successfully downloaded version:", releaseId);
            setStoragedJsVersion(releaseId);
            setReloadNeeded(true);
          })
          .catch(() => {
            // storing failed, try to delete js, and reload (this will trigger redownload in cordova index.html)
            // so user will get fresh js even if some error in indexedDB
            deleteJsStorage().then(() => {
              setReloadNeeded(true);
            });
          });
      })
      .catch((error) => {
        console.error("[ReleaseUpdater] Failed to download version:", releaseId, error);
        const isAbort = error?.name === "AbortError";
        const isFetchFailed = error?.name === "TypeError" && error?.message?.includes("Failed to fetch");
        if (!isAbort && !isFetchFailed) {
          sentryCapture("releaseUpdater download failed", { error });
        }

        // Exponential backoff: 10s, 30s, 1.5m
        const delayMs = 10000 * Math.pow(3, retryCount);
        console.log(`[ReleaseUpdater] Retrying download in ${delayMs / 1000}s (attempt ${retryCount + 1})`);

        setTimeout(() => {
          setRetryCount((prev) => prev + 1);
        }, delayMs);
      })
      .finally(() => {
        isDownloadingRef.current = false;
      });

    // Cleanup function to abort download
    return () => {
      if (!abortController.signal.aborted) {
        console.log("[ReleaseUpdater] Aborting download due to cleanup");
        abortController.abort();
      }
      isDownloadingRef.current = false;
    };
  }, [isLatest, releaseId, releaseIdPrev, reloadNeeded, retryCount, forceDownloadUpdate]);

  const isReloading = mainRoute && !isAssistantOpen && reloadNeeded;
  if (isReloading && !reloadOnce.current) {
    console.log("[ReleaseUpdater] Initiating page reload for new version");
    reloadOnce.current = true;
    windowReloadLimited(() => {}, {
      reason: "detected new releaseId - this should fade out once all users will update their releaseId in Redux"
    });
  }

  React.useEffect(() => {
    if (!isLatest) setNeedsUpdate(true);
  });

  if (!isReleaseReady || isReloading) {
    return <Splash />;
  } else {
    return <>{children}</>;
  }
};

export default ReleaseUpdater;
